// Copyright (c) StrangeBee 2024

import DraggableElement from '@Atoms/DraggableElement/DraggableElement';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { WidgetIcon } from '@Organisms/Dashboard/DashboardView/constants';
import { useDashboard } from '@Hooks/useDashboard';
import { DashboardDropWidgetResult, WIDGET_DND_TYPES } from '@Types';
import { Button, Space, Form } from 'antd';
import React, { useState, ReactElement } from 'react';
import { WidgetChartType, WidgetOutput } from 'thehive-sdk';
import { BarForm } from './Bar/BarForm';
import { capitalize } from '@Helpers/';
import { CounterForm } from './Counter/CounterForm';
import { Store } from 'antd/lib/form/interface';
import { DonutForm } from './Donut/DonutForm';
import { LineForm } from './Line/LineForm';
import { TextForm } from './Text/TextForm';
import { GaugeForm } from './Gauge/GaugeForm';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

type WidgetFormType =
    | typeof BarForm
    | typeof CounterForm
    | typeof DonutForm
    | typeof LineForm
    | typeof TextForm
    | typeof GaugeForm;

interface NewWidgetDrawerProps {
    getNewWidget: (values: Store) => WidgetOutput;
    WidgetForm: WidgetFormType;
    widgetType: WidgetChartType;
}

const NewWidgetDrawer = ({ getNewWidget, WidgetForm, widgetType }: NewWidgetDrawerProps): ReactElement => {
    const i = useLocalMessage();
    const [form] = Form.useForm();
    const [fieldsTouched, setFieldsTouched] = useState(false); // eslint-disable-line
    const [dropResult, setDropResult] = useState<any>(null);
    const [displayDrawer, setDisplayDrawer] = useState<boolean>(false);
    const { createWidget } = useDashboard();

    const openDrawer = (dropResult: DashboardDropWidgetResult) => {
        setDropResult(dropResult);
        setDisplayDrawer(true);
    };

    const resetDropResult = () => {
        setDropResult(null);
        setDisplayDrawer(false);
        form.resetFields();
    };

    const onFinish = (widgetData: Store): void => {
        const newWidget = getNewWidget(widgetData);

        const { hoveredRowIndex, hoveredWidgetIndex } = dropResult;

        createWidget?.(newWidget, hoveredRowIndex, hoveredWidgetIndex);
        resetDropResult();

        handleFooterClick();
    };

    const handleFooterClick = (): void => {
        form.validateFields().then(() => {
            form.submit();
        });
    };

    const title = (
        <Space>
            {WidgetIcon[widgetType]}
            {i(`dashboard.widget.${widgetType}.title`)}
        </Space>
    );

    return (
        <>
            <DraggableElement onDragFinish={openDrawer} dragType={WIDGET_DND_TYPES.WIDGET}>
                <THTooltip placement="bottom" title={capitalize(widgetType)}>
                    <Button icon={WidgetIcon[widgetType]} size="large" shape="circle" />
                </THTooltip>
            </DraggableElement>
            {displayDrawer && (
                <THDrawer
                    title={title}
                    defaultVisible={displayDrawer}
                    onClose={resetDropResult}
                    className="drawer-form"
                    width="50vw"
                >
                    {(toggleDrawer) => (
                        <WidgetForm
                            onCancel={() => {
                                form.resetFields();
                                setFieldsTouched(false);
                                toggleDrawer();
                                resetDropResult();
                            }}
                            onChange={() => {
                                setFieldsTouched(true);
                            }}
                            form={form}
                            onFinish={(values) => {
                                onFinish(values);
                                toggleDrawer();
                                setFieldsTouched(false);
                            }}
                        />
                    )}
                </THDrawer>
            )}
        </>
    );
};

export default NewWidgetDrawer;
